import React, { useState, useEffect } from 'react';
import { Search, Layers, ChevronDown, ChevronUp, X, Download, ExternalLink, ArrowDown, ArrowUp } from 'lucide-react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';

// Main Component for Synaptic Compartment Section on Landing Page
const SynapticCompartmentsSection = () => {
    const [compartmentsData, setCompartmentsData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCompartment, setSelectedCompartment] = useState(null);

    useEffect(() => {
        const fetchCompartmentsData = async () => {
            try {
                setIsLoading(true);
                const response = await fetch('/api/proteins/compartments');
                const data = await response.json();

                if (data.success) {
                    setCompartmentsData(data.data);
                } else {
                    setError(data.message || 'Failed to load compartment data');
                }
            } catch (err) {
                setError(`Error: ${err.message}`);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCompartmentsData();
    }, []);

    // Define colors for different compartments
    const getCompartmentColor = (compartment) => {
        switch (compartment) {
            case 'Presynaptic':
                return 'blue';
            case 'Postsynaptic':
                return 'green';
            case 'Synaptic_Vesicle':
                return 'purple';
            case 'Synaptosome':
                return 'amber';
            default:
                return 'gray';
        }
    };

    // Get color class for background based on compartment type
    const getBgColorClass = (compartment) => {
        const color = getCompartmentColor(compartment);
        switch (color) {
            case 'blue': return 'bg-blue-600';
            case 'green': return 'bg-green-600';
            case 'purple': return 'bg-purple-600';
            case 'amber': return 'bg-amber-600';
            default: return 'bg-gray-600';
        }
    };

    // Get color class for background with lighter shade (50)
    const getBgLightColorClass = (compartment) => {
        const color = getCompartmentColor(compartment);
        switch (color) {
            case 'blue': return 'bg-blue-50';
            case 'green': return 'bg-green-50';
            case 'purple': return 'bg-purple-50';
            case 'amber': return 'bg-amber-50';
            default: return 'bg-gray-50';
        }
    };

    // Get color class for text based on compartment type
    const getTextColorClass = (compartment) => {
        const color = getCompartmentColor(compartment);
        switch (color) {
            case 'blue': return 'text-blue-800';
            case 'green': return 'text-green-800';
            case 'purple': return 'text-purple-800';
            case 'amber': return 'text-amber-800';
            default: return 'text-gray-800';
        }
    };

    // Get a human-readable name for the compartment
    const getCompartmentDisplayName = (compartment) => {
        switch (compartment) {
            case 'Synaptic_Vesicle':
                return 'Synaptic Vesicles';
            default:
                return compartment;
        }
    };

    // Get description for the compartment
    const getCompartmentDescription = (compartment) => {
        switch (compartment) {
            case 'Presynaptic':
                return 'associated with the presynaptic terminal.';
            case 'Postsynaptic':
                return 'including postsynaptic density fractions and specific protein complexes.';
            case 'Synaptic_Vesicle':
                return 'associated with synaptic vesicles.';
            case 'Synaptosome':
                return 'found in the complete synaptosome fraction.';
            default:
                return 'Synaptic proteins.';
        }
    };

    // Handle clicking on a compartment card
    const handleCompartmentClick = (compartment) => {
        setSelectedCompartment(compartment);
    };

    // Close the protein explorer panel
    const handleCloseExplorer = () => {
        setSelectedCompartment(null);
    };

    if (isLoading) {
        return (
            <div className="py-12 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                        <p className="mt-4 text-gray-500">Loading compartment data...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="py-12 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-red-50 p-4 rounded-md">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <X className="h-5 w-5 text-red-400" />
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
                                <div className="mt-2 text-sm text-red-700">{error}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <section className="py-12 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="lg:text-center mb-10">
                    <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">Synapse Compartments</h2>
                    <p className="mt-2 text-3xl leading-8 font-bold tracking-tight text-gray-900 sm:text-4xl">
                        Detailed Synapse Protein Localization
                    </p>
                </div>

                <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
                    {compartmentsData.map((compartment) => {
                        // Define custom protein count values based on compartment type
                        let proteinCount;
                        switch (compartment.compartment) {
                            case 'Presynaptic':
                                proteinCount = 456;
                                break;
                            case 'Postsynaptic':
                                proteinCount = 1359;
                                break;
                            case 'Synaptic_Vesicle':
                                proteinCount = 1628;
                                break;
                            case 'Synaptosome':
                                proteinCount = 3443;
                                break;
                            default:
                                proteinCount = compartment.protein_count; // Fallback to original value
                        }

                        return (
                            <div
                                key={compartment.compartment}
                                className={`${getBgLightColorClass(compartment.compartment)} p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer`}
                                onClick={() => handleCompartmentClick(compartment.compartment)}
                            >
                                <div className={`flex items-center justify-center h-12 w-12 rounded-md ${getBgColorClass(compartment.compartment)} text-white mb-4`}>
                                    <Layers className="h-6 w-6" />
                                </div>
                                <h3 className={`text-lg font-medium ${getTextColorClass(compartment.compartment)}`}>
                                    {getCompartmentDisplayName(compartment.compartment)}
                                </h3>
                                <p className="mt-2 text-gray-600">{proteinCount} proteins {getCompartmentDescription(compartment.compartment)}</p>
                            </div>
                        );
                    })}
                </div>

                {/* Protein Explorer Panel - Shows when a compartment is selected */}
                {selectedCompartment && (
                    <CompartmentProteinExplorer
                        compartment={selectedCompartment}
                        onClose={handleCloseExplorer}
                        displayName={getCompartmentDisplayName(selectedCompartment)}
                        color={getCompartmentColor(selectedCompartment)}
                    />
                )}
            </div>
        </section>
    );
};

// Component to explore proteins within a compartment
const CompartmentProteinExplorer = ({ compartment, onClose, displayName, color }) => {
    const [proteins, setProteins] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [frequencyFilter, setFrequencyFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [expandedProteins, setExpandedProteins] = useState({});
    const proteinsPerPage = 20;

    // Add sorting state
    const [sortField, setSortField] = useState('HumanName');
    const [sortDirection, setSortDirection] = useState('asc');

    useEffect(() => {
        const fetchProteins = async () => {
            try {
                setIsLoading(true);
                const response = await fetch(`/api/proteins/compartment/${compartment}`);
                const data = await response.json();

                if (data.success) {
                    setProteins(data.data);
                } else {
                    setError(data.message || 'Failed to load proteins');
                }
            } catch (err) {
                setError(`Error: ${err.message}`);
            } finally {
                setIsLoading(false);
            }
        };

        fetchProteins();
    }, [compartment]);

    // Helper functions for color classes
    const getHeaderBgClass = () => {
        switch (color) {
            case 'blue': return 'bg-blue-100';
            case 'green': return 'bg-green-100';
            case 'purple': return 'bg-purple-100';
            case 'amber': return 'bg-amber-100';
            default: return 'bg-gray-100';
        }
    };

    const getHeaderTextClass = () => {
        switch (color) {
            case 'blue': return 'text-blue-800';
            case 'green': return 'text-green-800';
            case 'purple': return 'text-purple-800';
            case 'amber': return 'text-amber-800';
            default: return 'text-gray-800';
        }
    };

    const getButtonBgClass = () => {
        switch (color) {
            case 'blue': return 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500';
            case 'green': return 'bg-green-600 hover:bg-green-700 focus:ring-green-500';
            case 'purple': return 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500';
            case 'amber': return 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500';
            default: return 'bg-gray-600 hover:bg-gray-700 focus:ring-gray-500';
        }
    };

    // Handle column header click for sorting
    const handleSort = (field) => {
        // If clicking the same field, toggle direction
        if (field === sortField) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // New field, set it and default to ascending
            setSortField(field);
            setSortDirection('asc');
        }

        // Reset to first page when sorting changes
        setPage(1);
    };

    // Render sort indicator
    const renderSortIndicator = (field) => {
        if (sortField !== field) {
            return (
                <span className="ml-1 text-gray-400 inline-block">
                    <ChevronDown className="h-4 w-4" />
                </span>
            );
        }

        return sortDirection === 'asc' ? (
            <span className="ml-1 text-blue-600 inline-block">
                <ArrowUp className="h-4 w-4" />
            </span>
        ) : (
            <span className="ml-1 text-blue-600 inline-block">
                <ArrowDown className="h-4 w-4" />
            </span>
        );
    };

    // Filter proteins based on search term and frequency filter
    const filteredProteins = proteins.filter(protein => {
        // Handle search term matching across all relevant fields
        const matchesSearch = searchTerm === '' ||
            (protein.HumanName && protein.HumanName.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (protein.HumanEntrez && protein.HumanEntrez.toString().includes(searchTerm)) ||
            (protein.MouseEntrez && protein.MouseEntrez.toString().includes(searchTerm)) ||
            (protein.RatEntrez && protein.RatEntrez.toString().includes(searchTerm)) ||
            (protein.MGI && protein.MGI.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesFrequency = frequencyFilter === 'all' || protein.frequency === frequencyFilter;

        return matchesSearch && matchesFrequency;
    });

    // Sort filtered proteins
    const sortedProteins = [...filteredProteins].sort((a, b) => {
        const fieldA = a[sortField];
        const fieldB = b[sortField];

        // Check if the fields exist before comparing
        if (fieldA === null || fieldA === undefined) return 1;
        if (fieldB === null || fieldB === undefined) return -1;

        // Different sorting for strings vs numbers
        let comparison = 0;
        if (typeof fieldA === 'string') {
            comparison = fieldA.localeCompare(fieldB);
        } else {
            comparison = fieldA - fieldB;
        }

        // Apply sort direction
        return sortDirection === 'asc' ? comparison : -comparison;
    });

    // Pagination
    const totalPages = Math.ceil(sortedProteins.length / proteinsPerPage);
    const paginatedProteins = sortedProteins.slice(
        (page - 1) * proteinsPerPage,
        page * proteinsPerPage
    );

    // Toggle protein expansion
    const toggleProteinExpansion = (geneId) => {
        setExpandedProteins(prev => ({
            ...prev,
            [geneId]: !prev[geneId]
        }));
    };


    // Update the search handler to reset the page
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setPage(1); // Reset to page 1 when search term changes
    };

    // Update the frequency filter handler
    const handleFrequencyFilterChange = (e) => {
        setFrequencyFilter(e.target.value);
        setPage(1); // Reset to page 1 when filter changes
    };

    // Handle downloading CSV
    const handleDownloadCSV = () => {
        window.location.href = `/api/proteins/compartment/${compartment}/csv`;
    };

    return (
        <div className="mt-12 bg-white rounded-lg shadow-lg overflow-hidden">
            <div className={`${getHeaderBgClass()} px-6 py-4 flex items-center justify-between`}>
                <h3 className={`text-xl font-semibold ${getHeaderTextClass()}`}>
                    {displayName} Proteins ({filteredProteins.length})
                </h3>
                <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                    <X className="h-6 w-6" />
                </button>
            </div>

            <div className="p-6">
                {/* Search and filters */}
                <div className="mb-6 flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[250px]">
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Search by gene name, Entrez ID, or MGI ID..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                            />
                        </div>
                    </div>

                    <div>
                        <select
                            className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                            value={frequencyFilter}
                            onChange={handleFrequencyFilterChange}
                        >
                            <option value="all">All Frequencies</option>
                            <option value="1">Found in 1 paper</option>
                            <option value="2-3">Found in 2-3 papers</option>
                            <option value="4-9">Found in 4-9 papers</option>
                            <option value=">10">Found in 10+ papers</option>
                        </select>
                    </div>

                    <button
                        onClick={handleDownloadCSV}
                        className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${getButtonBgClass()} focus:outline-none focus:ring-2 focus:ring-offset-2`}
                    >
                        <Download className="h-4 w-4 mr-2" />
                        Download CSV
                    </button>
                </div>

                {isLoading ? (
                    <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                        <p className="mt-4 text-gray-500">Loading proteins...</p>
                    </div>
                ) : error ? (
                    <div className="bg-red-50 p-4 rounded-md">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <X className="h-5 w-5 text-red-400" />
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">Error loading proteins</h3>
                                <div className="mt-2 text-sm text-red-700">{error}</div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {filteredProteins.length === 0 ? (
                            <div className="text-center py-8">
                                <p className="text-gray-500">No proteins match your search criteria.</p>
                            </div>
                        ) : (
                            <>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th
                                                    scope="col"
                                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                                    onClick={() => handleSort('HumanName')}
                                                >
                                                    <div className="flex items-center">
                                                        Gene Symbol
                                                        {renderSortIndicator('HumanName')}
                                                    </div>
                                                </th>
                                                <th
                                                    scope="col"
                                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                                    onClick={() => handleSort('HumanEntrez')}
                                                >
                                                    <div className="flex items-center">
                                                        Human Entrez
                                                        {renderSortIndicator('HumanEntrez')}
                                                    </div>
                                                </th>
                                                <th
                                                    scope="col"
                                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                                    onClick={() => handleSort('paper_count')}
                                                >
                                                    <div className="flex items-center">
                                                        Paper Count
                                                        {renderSortIndicator('paper_count')}
                                                    </div>
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Frequency
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Details
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {paginatedProteins.map((protein) => (
                                                <React.Fragment key={protein.GeneID}>
                                                    <tr className="hover:bg-gray-50">
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            <a href={`/gene-search?q=${encodeURIComponent(protein.HumanName)}`} className="text-blue-600 hover:text-blue-800 italic">
                                                                {protein.HumanName}
                                                            </a>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            <a
                                                                href={`https://www.ncbi.nlm.nih.gov/gene/${protein.HumanEntrez}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-blue-600 hover:text-blue-800 flex items-center"
                                                            >
                                                                {protein.HumanEntrez}
                                                                <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1" />
                                                            </a>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            <div className="relative group">
                                                                <div className="cursor-help flex items-center">
                                                                    {protein.paper_count}
                                                                </div>
                                                                <div className="absolute z-10 hidden group-hover:block bg-black text-white text-xs rounded py-1 px-2 right-0 bottom-full mb-1 min-w-[200px] shadow-lg">
                                                                    Click on the Gene ID to view more information about the papers.
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${protein.frequency === '>10' ? 'bg-green-100 text-green-800' :
                                                                    protein.frequency === '4-9' ? 'bg-blue-100 text-blue-800' :
                                                                        protein.frequency === '2-3' ? 'bg-yellow-100 text-yellow-800' :
                                                                            'bg-gray-100 text-gray-800'
                                                                }`}>
                                                                {protein.frequency}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            <button
                                                                onClick={() => toggleProteinExpansion(protein.GeneID)}
                                                                className="text-blue-600 hover:text-blue-900 focus:outline-none"
                                                            >
                                                                {expandedProteins[protein.GeneID] ? (
                                                                    <ChevronUp className="h-5 w-5" />
                                                                ) : (
                                                                    <ChevronDown className="h-5 w-5" />
                                                                )}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                    {expandedProteins[protein.GeneID] && (
                                                        <tr>
                                                            <td colSpan="5" className="px-6 py-4 bg-gray-50">
                                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                                    <div>
                                                                        <h4 className="text-sm font-medium text-gray-900 mb-2">Additional Identifiers</h4>
                                                                        <table className="min-w-full">
                                                                            <tbody className="divide-y divide-gray-200">
                                                                                <tr>
                                                                                    <td className="py-1 pr-4 text-sm font-medium text-gray-500">Mouse Entrez:</td>
                                                                                    <td className="py-1 text-sm text-gray-900">
                                                                                        <a
                                                                                            href={`https://www.ncbi.nlm.nih.gov/gene/${protein.MouseEntrez}`}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            className="text-blue-600 hover:text-blue-800"
                                                                                        >
                                                                                            {protein.MouseEntrez}<ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-1 inline" />
                                                                                        </a>
                                                                                    </td>
                                                                                </tr>
                                                                                <tr>
                                                                                    <td className="py-1 pr-4 text-sm font-medium text-gray-500">Rat Entrez:</td>
                                                                                    <td className="py-1 text-sm text-gray-900">
                                                                                        <a
                                                                                            href={`https://www.ncbi.nlm.nih.gov/gene/${protein.RatEntrez}`}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            className="text-blue-600 hover:text-blue-800"
                                                                                        >
                                                                                            {protein.RatEntrez}<ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-1 inline" />
                                                                                        </a>
                                                                                    </td>
                                                                                </tr>
                                                                                <tr>
                                                                                    <td className="py-1 pr-4 text-sm font-medium text-gray-500">MGI:</td>
                                                                                    <td className="py-1 text-sm text-gray-900">
                                                                                        <a
                                                                                            href={`https://www.informatics.jax.org/marker/${protein.MGI}`}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            className="text-blue-600 hover:text-blue-800"
                                                                                        >
                                                                                            {protein.MGI}<ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-1 inline" />
                                                                                        </a>
                                                                                    </td>
                                                                                </tr>
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                    <div>
                                                                        <h4 className="text-sm font-medium text-gray-900 mb-2">Links</h4>
                                                                        <div className="space-y-2">
                                                                            <a
                                                                                href={`/gene-search?q=${encodeURIComponent(protein.HumanName)}`}
                                                                                className="inline-flex items-center text-blue-600 hover:text-blue-800"
                                                                            >
                                                                                <ExternalLink className="h-4 w-4 mr-1" />
                                                                                View detailed gene information
                                                                            </a>
                                                                            <div>
                                                                                <a
                                                                                    href={`https://www.uniprot.org/uniprot/?query=${encodeURIComponent(protein.HumanName)}`}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="inline-flex items-center text-blue-600 hover:text-blue-800"
                                                                                >
                                                                                    <ExternalLink className="h-4 w-4 mr-1" />
                                                                                    UniProt<ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-1 inline" />
                                                                                </a>
                                                                            </div>
                                                                            <div>
                                                                                <a
                                                                                    href={`https://www.proteinatlas.org/search/${encodeURIComponent(protein.HumanName)}`}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    lassName="inline-flex items-center text-blue-600 hover:text-blue-800"
                                                                                >
                                                                                    <ExternalLink className="h-4 w-4 mr-1" />
                                                                                    Human Protein Atlas<ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-1 inline" />
                                                                                </a>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-6 mt-4">
                                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                            <div>
                                                <p className="text-sm text-gray-700">
                                                    Showing <span className="font-medium">{(page - 1) * proteinsPerPage + 1}</span> to{' '}
                                                    <span className="font-medium">
                                                        {Math.min(page * proteinsPerPage, filteredProteins.length)}
                                                    </span>{' '}
                                                    of <span className="font-medium">{filteredProteins.length}</span> results
                                                </p>
                                            </div>
                                            <div>
                                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                                    <button
                                                        onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                                                        disabled={page === 1}
                                                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${page === 1 ? 'cursor-not-allowed opacity-50' : ''
                                                            }`}
                                                    >
                                                        <span className="sr-only">Previous</span>
                                                        <ChevronDown className="h-5 w-5 transform rotate-90" />
                                                    </button>

                                                    {Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
                                                        // Show pages around the current page
                                                        let pageNumber;
                                                        if (totalPages <= 5) {
                                                            pageNumber = i + 1;
                                                        } else if (page <= 3) {
                                                            pageNumber = i + 1;
                                                        } else if (page >= totalPages - 2) {
                                                            pageNumber = totalPages - 4 + i;
                                                        } else {
                                                            pageNumber = page - 2 + i;
                                                        }

                                                        return (
                                                            <button
                                                                key={pageNumber}
                                                                onClick={() => setPage(pageNumber)}
                                                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${page === pageNumber
                                                                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                                    }`}
                                                            >
                                                                {pageNumber}
                                                            </button>
                                                        );
                                                    })}

                                                    <button
                                                        onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                                                        disabled={page === totalPages}
                                                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${page === totalPages ? 'cursor-not-allowed opacity-50' : ''
                                                            }`}
                                                    >
                                                        <span className="sr-only">Next</span>
                                                        <ChevronDown className="h-5 w-5 transform -rotate-90" />
                                                    </button>
                                                </nav>

                                                {/* Sort indicator tooltip */}
                                                <div className="mt-4 text-xs text-gray-500 italic">
                                                    <span>Click on column headers to sort the table</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default SynapticCompartmentsSection;